
import type { Config, ConfigFieldSchema, ConfigSchema } from '@empathyco/playboard-core-domain'
import type { Dictionary, ServiceConfigFieldValidityState } from '../../models'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { PermissionLevelEnum } from '@empathybroker/tsclient-play/dist/enums/permission-level.enum'
import { ConfigFieldVisibilityEnum } from '@empathyco/playboard-core-domain'
import { deepEqual } from '@empathyco/x-utils'

import { defineComponent } from 'vue'

import { mapActions, mapGetters } from 'vuex'

import ServiceConfigActionsBar from '../../components/instance-management/service-config-actions-bar.component.vue'
import ServiceConfigField from '../../components/instance-management/service-config-field.component.vue'

import { InstanceServiceIdEnum } from '../../enums'

import { ROUTER_CONSTS } from '../../router/router.consts'

import { APP_CONSTS } from '../../store/app/app.consts'
import { INSTANCE_MANAGEMENT_CONSTS } from '../../store/instance-management/instance-management.consts'

import { FeaturesTreeUtils, InstanceManagementUtils } from '../../utils'

export default defineComponent({
  name: 'InstanceServiceConfigForm',
  components: {
    ServiceConfigField,
    ServiceConfigActionsBar,
  },
  computed: {
    ...mapGetters(INSTANCE_MANAGEMENT_CONSTS.moduleName, {
      serviceConfigs: INSTANCE_MANAGEMENT_CONSTS.getters.serviceConfigs,
    }),
    ...mapGetters(APP_CONSTS.moduleName, {
      featuresTree: APP_CONSTS.getters.featuresTree,
    }),
    readonly(): boolean {
      const featureNode = FeaturesTreeUtils.getFeatureNode(
        this.$route.meta.featureId,
        this.featuresTree[DomainEnum.GLOBAL],
      )
      return featureNode.permissionLevel !== PermissionLevelEnum.WRITE
    },
    config(): Config {
      return this.serviceConfigs.find(
        ({ service }: Config) => service === this.$route.params[ROUTER_CONSTS.params.service],
      )
    },
    validityState(): ServiceConfigFieldValidityState {
      return InstanceManagementUtils.getServiceConfigFieldValidityState(
        this.config.schema,
        this.formData,
      )
    },
    valid(): boolean {
      return !this.validityState.errors.length
    },
    formDataChanged(): boolean {
      const data = InstanceManagementUtils.getServiceConfigFieldFormData(
        this.config.schema,
        this.config.data,
      )
      return !deepEqual(this.formData, data)
    },
    schema(): ConfigSchema {
      const paramChild = this.$route.params[ROUTER_CONSTS.params.child]
      if (!paramChild) {
        return this.config.schema
      }
      const schema = { ...this.config.schema }
      schema.properties = Object.entries(schema.properties ?? {}).reduce(
        (
          properties: Dictionary<ConfigFieldSchema>,
          [propertyId, propertySchema]: [string, ConfigFieldSchema],
        ) => {
          properties[propertyId] = {
            ...propertySchema,
            front_metadata: {
              ...propertySchema.front_metadata,
              visibility:
                propertyId === paramChild
                  ? propertySchema.front_metadata?.visibility
                  : ConfigFieldVisibilityEnum.HIDDEN,
            },
          }
          return properties
        },
        {},
      )

      return schema
    },
  },
  data() {
    return {
      baseClass: 'epb-instance-service-config-form',
      formData: undefined,
      messages: this.$epb.messages.instanceManagement.serviceConfig,
      InstanceServiceIdEnum,
    }
  },
  methods: {
    ...mapActions(INSTANCE_MANAGEMENT_CONSTS.moduleName, {
      updateServiceConfig: INSTANCE_MANAGEMENT_CONSTS.actions.updateServiceConfig,
      setEnabledServiceConfig: INSTANCE_MANAGEMENT_CONSTS.actions.setEnabledServiceConfig,
    }),
    async save(): Promise<void> {
      if (!this.formDataChanged) {
        return
      }
      this.$emit('updateLoading', { visible: true })
      try {
        const formConfig = this.config.clone()
        formConfig.data = this.formData
        await this.updateServiceConfig(formConfig)
        this.$emit('showSnackbar', this.messages.actions.save.success)
      } catch (error) {
        this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
      } finally {
        this.$emit('updateLoading', { visible: false })
      }
    },
    async setEnabled(enabled: boolean): Promise<void> {
      if (enabled === this.config.enabled) {
        return
      }
      this.$emit('updateLoading', { visible: true })
      try {
        await this.setEnabledServiceConfig({ config: this.config, enabled })
        const actionMessages = enabled
          ? this.messages.actions.enable
          : this.messages.actions.disable
        this.$emit('showSnackbar', actionMessages.success)
      } catch (error) {
        this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
      } finally {
        this.$emit('updateLoading', { visible: false })
      }
    },
  },
  watch: {
    config: {
      handler(config: Config): void {
        this.formData = InstanceManagementUtils.getServiceConfigFieldFormData(
          config.schema,
          config.data,
        )
      },
      deep: true,
      immediate: true,
    },
  },
})
